import React, { useEffect, useState, useCallback } from 'react';
import api, { fileUrl } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Search, MoreHorizontal, EyeOff, CheckCircle2, PackageX, Plus, Minus, Trash2, MapPin, Image as ImageIcon, Flag,
} from 'lucide-react';
import { toast } from 'sonner';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'expired', label: 'Expired' },
  { key: 'reported', label: 'Reported' },
  { key: 'removed', label: 'Removed' },
];
const STATUS_COLOR = { active: '#7A9A78', upcoming: '#D99E4A', ended: '#8A8F87', hidden: '#C86F53' };

const fmt = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

export default function PostsPanel() {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/posts', { params: { status: filter, q: q || undefined } });
      setPosts(r.data);
    } catch { toast.error('Could not load posts'); }
    finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const act = async (pin, action, hours) => {
    if (action === 'remove' && !window.confirm('Permanently delete this post?')) return;
    try {
      await api.post(`/admin/posts/${pin.id}/action`, { action, hours });
      toast.success(`Post ${action.replace('_', ' ')}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div data-testid="posts-panel">
      <h2 className="font-display font-bold text-2xl text-brand-ink mb-1">Post moderation</h2>
      <p className="text-brand-slate text-sm mb-5">Review, hide, extend or remove any post.</p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map(f => (
          <button key={f.key} data-testid={`posts-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${filter === f.key ? 'bg-brand text-white' : 'glass-panel text-brand-ink hover:bg-white'}`}>
            {f.label}
          </button>
        ))}
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-dim" />
          <Input data-testid="posts-search" value={q} onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Search title, location, user, or post ID…" className="pl-9 rounded-full bg-white/70" />
        </div>
      </div>

      <div className="glass-panel rounded-3xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[60px_1fr_120px_120px_140px_44px] gap-3 px-4 py-3 text-[11px] uppercase tracking-wider text-brand-dim border-b border-black/5">
          <div>Photo</div><div>Title / poster</div><div>Status</div><div>Created</div><div>Expires</div><div></div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-brand-slate text-sm">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="p-8 text-center text-brand-slate text-sm" data-testid="posts-empty">No posts match.</div>
        ) : posts.map(p => (
          <div key={p.id} data-testid={`post-row-${p.id}`} className="grid grid-cols-[60px_1fr_auto] md:grid-cols-[60px_1fr_120px_120px_140px_44px] gap-3 px-4 py-3 items-center border-b border-black/5 last:border-0">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-black/5 flex items-center justify-center">
              {p.photos?.[0] && p.photos[0] !== 'default_yard_sale'
                ? <img src={fileUrl(p.photos[0])} alt="" className="w-full h-full object-cover" />
                : <ImageIcon className="w-5 h-5 text-brand-dim" />}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-brand-ink truncate flex items-center gap-1.5">
                {p.title}
                {p.is_reported && <Flag className="w-3.5 h-3.5 text-[#C86F53]" />}
              </div>
              <div className="text-xs text-brand-slate truncate">
                {p.category === 'free' ? 'Free' : 'Yard Sale'} · @{p.username || 'neighbor'} · {p.address || 'no address'}
              </div>
            </div>
            <Badge className="border-0 justify-self-start md:justify-self-auto" style={{ background: (STATUS_COLOR[p.status] || '#888') + '22', color: STATUS_COLOR[p.status] || '#888' }}>{p.status}</Badge>
            <div className="hidden md:block text-xs text-brand-slate">{fmt(p.created_at)}</div>
            <div className="hidden md:block text-xs text-brand-slate">{fmt(p.ends_at)}</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid={`post-actions-${p.id}`} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"><MoreHorizontal className="w-4 h-4" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-2xl">
                {p.status === 'hidden'
                  ? <DropdownMenuItem data-testid={`post-approve-${p.id}`} onClick={() => act(p, 'approve')}><CheckCircle2 className="w-4 h-4 mr-2 text-[#7A9A78]" /> Approve / unhide</DropdownMenuItem>
                  : <DropdownMenuItem data-testid={`post-hide-${p.id}`} onClick={() => act(p, 'hide')}><EyeOff className="w-4 h-4 mr-2" /> Hide</DropdownMenuItem>}
                <DropdownMenuItem onClick={() => act(p, 'mark_gone')}><PackageX className="w-4 h-4 mr-2" /> Mark as gone</DropdownMenuItem>
                <DropdownMenuItem onClick={() => act(p, 'extend', 24)}><Plus className="w-4 h-4 mr-2" /> Extend +24h</DropdownMenuItem>
                <DropdownMenuItem onClick={() => act(p, 'shorten', 24)}><Minus className="w-4 h-4 mr-2" /> Shorten -24h</DropdownMenuItem>
                <DropdownMenuItem asChild><a href={`/?pin=${p.id}`} target="_blank" rel="noopener noreferrer"><MapPin className="w-4 h-4 mr-2" /> View on map</a></DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid={`post-remove-${p.id}`} onClick={() => act(p, 'remove')} className="text-[#C86F53]"><Trash2 className="w-4 h-4 mr-2" /> Remove permanently</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>
    </div>
  );
}
